// @flow
import * as React from 'react';
import { useEffect, useState } from 'react';
import { PAGE_RESET_PASSWORD } from "../../locales/pages/namespaces";
import { useTranslation } from "react-i18next";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { API } from "../../helpers/constants";
import { getRouteUrl } from "../../helpers/getRouteUrl";
import {
	Typography,
	Button,
	TextField,
	InputAdornment,
	IconButton,
	Stack,
	FormControl,
	FormHelperText,
	FormLabel, Grid, OutlinedInput
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { ROUTE_PAGE_HOME } from "../../routers/routes";
import moment from "moment";
import useQueryParams from "../../hooks/useQueryParams";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../helpers/errorHandling";
import useUserAction from "../../hooks/user/useUserAction";
import useUserData from "../../hooks/user/useUserData";
import { enqueueSnackbar } from "notistack";
import AuthWrapper from "../../theme/mantis/sections/auth/authWrapper";
import VisibleEyeOff from "../../assets/images/icons/login/visibleEyeOff";
import VisibleEyeOn from "../../assets/images/icons/login/visibleEyeOn";
import ArrowDownCircle from "../../assets/images/icons/buttonsIcons/arrowDownCircle";
import AuthCardWrapper from '../../../src/theme/mantis/sections/auth/authCardWrapper';
const ResetPassword = () => {
	const { userLogout, setUserAlterPassword } = useUserAction()
	const { loading, alterPasswordOnLogin, userId, isLoggedIn } = useUserData()
	const queryParams = useQueryParams()
	const [recoveryPwdTime, setRecoveryPwdTime] = useState(queryParams.get('recoveryPwdTime') ?? "")
	const [resetTime, setResetTime] = useState(queryParams.get('resetTime') ?? "")
	const resetType = queryParams.get('resetType') ?? ""
	const email = queryParams.get('email') ?? ""
	const [showPassword, setShowPassword] = useState(false);

	const history = useHistory()
	const { t } = useTranslation(PAGE_RESET_PASSWORD)

	useEffect(() => {
		document.title = t('IMS Financial Consulting | Reset Password')
	}, [t])

	const validationSchema = yup.object({
		oneTimePassword: !alterPasswordOnLogin && !userId ? yup.string().required(t('Required!')).max(6, t('Enter exactly 6 characters.')).min(6, t('Enter exactly 6 characters.')) : yup.string().notRequired(),
		password: yup.string()
			.required(t('Required!'))
			.matches(/^.{60}$|(^((?=.*?[A-Z])(?=.*?[a-z])(?=.*?\d)|(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[^a-zA-Z\d])|(?=.*?[A-Z])(?=.*?\d)(?=.*?[^a-zA-Z\d])|(?=.*?[a-z])(?=.*?\d)(?=.*?[^a-zA-Z\d])).{7,16}$)/,
				t('Password must be between 7-16 latin characters and contain at least 3 of the following. -Upper case character -Lower case character -Numeric digit -Special character')),
		confirmPassword: yup.string()
			.required(t('Required!'))
			.oneOf([yup.ref('password'), null], t('Passwords must match')),
	})

	let timeNow = new Date();
	let recoverUntil = moment(recoveryPwdTime).add(resetTime, 'minutes').toDate()

	const onSubmit = (values) => {
		if (moment(timeNow).isBefore(recoverUntil) || moment(timeNow).isSame(recoverUntil) || userId) {
			axios({
				method: 'post',
				data: {
					oneTimePassword: values.oneTimePassword,
					password: values.password,
					UserId: userId
				},
				headers: { 'Content-Type': 'application/json' },
				url: `${API}/store/new/password`
			}).then(response => {
				history.push(getRouteUrl(ROUTE_PAGE_HOME))
				setUserAlterPassword(response.data.AlterPassword === 1)
				enqueueSnackbar(t('Password changed successfully!'), {
					variant: 'success'
				}
				)
			}).catch((error) => {
				enqueueSnackbar(handleAxiosErrors(error, t('Wrong six-digit code!')), {
					variant: 'error'
				}
				)
				logoutUnauthorizedUser(error, isLoggedIn, userLogout)
			})
		} else {
			//userLogout()
			enqueueSnackbar(t('The six digit number has expired!'), {
				variant: 'warning'
			}
			)
		}
	}

	const newSixDigitCode = () => {
		axios({
			method: 'get',
			headers: { 'Content-Type': 'application/json' },
			url: `${API}/request/new-password?email=${email}`
		}).then(response => {
			setResetTime(response.data.resetTime)
			setRecoveryPwdTime(response.data.recoveryPwdTime)
			enqueueSnackbar(t('Six-digit code send successfully!'), {
				variant: 'success'
			}
			)
		}).catch(error => {
			enqueueSnackbar(handleAxiosErrors(error, t('Failed to send six-digit code!')), {
				variant: 'error'
			}
			)
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}

	const formik = useFormik({
		initialValues: {
			email: '',
			oneTimePassword: '',
			password: '',
			confirmPassword: '',
			recoveryPwdTime: email?.recoveryPwdTime,
			resetTime: email?.resetTime,
			resetType: ''
		},
		validationSchema: validationSchema,
		onSubmit: onSubmit
	})

	const handleClickShowPassword = () => setShowPassword((show) => !show);
	const handleMouseDownPassword = (event) => {
		event.preventDefault();
	};

	return (
		<>
			<AuthCardWrapper>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography className='color-default title-sm' sx={{ m: '27px auto 11px auto' }}>
							{t('Reset Password')}
						</Typography>
					</Grid>

					<Grid item xs={12}>
						<FormikProvider value={formik}>
							<Grid container spacing={3}>
								<Grid item xs={12}>
									<Stack spacing={1}>
										{resetType !== 'Email' && !alterPasswordOnLogin && !userId &&
											<>
												<FormControl
													variant="standard"
													fullWidth
													error={
														formik.touched["oneTimePassword"] &&
														Boolean(formik.errors["oneTimePassword"])
													}
													sx={{ mb: 2 }}
												>
													<FormLabel
														sx={{ margin: '0 0 10px 0', textAlign: 'left' }}
														className='p-xsfs color-default'
														htmlFor="oneTimePassword"
														required>
															{t('Enter the six-digit code you received in the email')}
													</FormLabel>


													<OutlinedInput
														id="outlined-name"
														name={`oneTimePassword`}
														value={formik.values.oneTimePassword}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														required
														error={formik.touched.oneTimePassword && Boolean(formik.errors.oneTimePassword)}
														color={"grey"}
														size={'small'}
														fullWidth
														className='border-neutral-dark-radius'
														sx={{ minHeight: 'unset' }}
														inputProps={{
															style: {
																fontSize: '14px',
																padding: '10px',
																minHeight: 'unset'
															},
														}}
													/>
													{formik.touched.oneTimePassword && Boolean(formik.errors.oneTimePassword) && (
														<FormHelperText error id="standard-weight-helper-text-email-login">
															{formik.errors.oneTimePassword}
														</FormHelperText>
													)}
												</FormControl>
											</>
										}
									</Stack>
								</Grid>
								<Grid item xs={12}>
									<Stack spacing={1}>
										<FormControl
											variant="standard"
											fullWidth
											error={
												formik.touched["password"] &&
												Boolean(formik.errors["password"])
											}
											sx={{ mb: 2 }}
										>
											<FormLabel sx={{ margin: '6px 0 10px 0', textAlign: 'left' }} className='p-xsfs color-default' htmlFor="password" required>
												{t('New Password')}
											</FormLabel>

											<OutlinedInput
												type={showPassword ? 'text' : 'password'}
												value={formik.values.password}
												name={`password`}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												fullWidth
												autoComplete="new-password"
												color={"grey"}
												size={'small'}
												required
												error={formik.touched.password && Boolean(formik.errors.password)}
												className='border-neutral-dark-radius'
												sx={{ minHeight: 'unset' }}
												inputProps={{
													style: {
														fontSize: '14px',
														padding: '10px',
														minHeight: 'unset'
													},
												}}
												startAdornment={
													<InputAdornment position="end">
														<IconButton
															aria-label="toggle password visibility"
															onClick={handleClickShowPassword}
															onMouseDown={handleMouseDownPassword}
															edge="end"
														>
															{showPassword ? <VisibleEyeOff /> : <VisibleEyeOn />}
														</IconButton>
													</InputAdornment>
												}
											/>
											{formik.touched.password && Boolean(formik.errors.password) && (
												<FormHelperText error id="standard-weight-helper-text-email-login">
													{formik.errors.password}
												</FormHelperText>
											)}

										</FormControl>
									</Stack>
								</Grid>




								<Grid item xs={12}>
									<Stack spacing={1}>
										<FormControl
											variant="standard"
											fullWidth
											error={
												formik.touched["confirmPassword"] &&
												Boolean(formik.errors["confirmPassword"])
											}
											sx={{ mb: 2 }}
										>

											<FormLabel sx={{ margin: '6px 0 10px 0', textAlign: 'left' }} className='p-xsfs color-default' htmlFor="password" required>
												{t('Confirm Password')}
											</FormLabel>

											<OutlinedInput
												type={showPassword ? 'text' : 'password'}
												value={formik.values.confirmPassword}
												name={`confirmPassword`}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												fullWidth
												autoComplete="on"
												color={"grey"}
												size={'small'}
												required
												error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
												startAdornment={
													<InputAdornment position="end">
														<IconButton
															aria-label="toggle password visibility"
															onClick={handleClickShowPassword}
															onMouseDown={handleMouseDownPassword}
															edge="end"
														>
															{showPassword ? <VisibleEyeOff /> : <VisibleEyeOn />}
														</IconButton>
													</InputAdornment>
												}

												className='border-neutral-dark-radius'
												sx={{ minHeight: 'unset', marginBottom: '8px' }}
												inputProps={{
													style: {
														fontSize: '14px',
														padding: '10px',
														minHeight: 'unset'
													},
												}}

											/>
											{formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword) && (
												<FormHelperText error id="standard-weight-helper-text-email-login">
													{formik.errors.confirmPassword}
												</FormHelperText>
											)}
										</FormControl>
									</Stack>
								</Grid>
								<Grid item xs={12} textAlign={'center'}>


									<Button
										disableElevation
										disabled={loading}
										fullWidth
										type="submit"
										sx={{ p: '14px 0', marginBottom: '4px' }}
										size={"small"}
										variant={"contained"}
										className='button-primary'
										onClick={formik.submitForm}
									>
										{t('Reset Password')}
									</Button>

								</Grid>
							</Grid>
						</FormikProvider>
					</Grid>


					<Grid item xs={12}
						//alignItems={'center'}
						textAlign={'-webkit-center'}
					>
						{!alterPasswordOnLogin && !userId && (
							<Typography
								sx={{ marginBottom: '36px' }}
								className='p-sm color-default'
								onClick={newSixDigitCode}
							>
								{t('Send me new Code')}
							</Typography>
						)}
					</Grid>


				</Grid>
			</AuthCardWrapper>
		</>
	)
}

export default ResetPassword;
